/* eslint-disable jsx-a11y/alt-text */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Utility from "../utility/utility";
import { useRest, CALL_TYPES } from "../services/rest/api";
import { BASE_URL } from "../services/environment";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resettingAllRooms, setResettingAllRooms] = useState(false);
  const [restartingServer, setRestartingServer] = useState(false);

  // Check if admin is authenticated
  useEffect(() => {
    const adminToken = localStorage.getItem("adminApiKey");
    if (!adminToken) {
      Utility.showToast("error", "Please login as admin first");
      navigate("/admin/auth");
      return;
    }
    
    fetchRooms();
  }, [navigate]);

  // Fetch rooms data
  const { fetchData: fetchRoomsData } = useRest({
    URL: "/api/admin/rooms",
    CALL_TYPE: CALL_TYPES.GET,
    PAYLOAD: {
      showSuccessToast: false
    },
    headers: {
      'admin-id': localStorage.getItem('adminId'),
      'api-key': localStorage.getItem('adminApiKey')
    },
    lazy: true,
    onSuccess: (data) => {
      if (data.status_code === 200) {
        setRooms(data.data.rooms || []);
        // setStats(data.data.statistics);
        setLoading(false);
      } else {
        Utility.showToast("error", data.message);
        if (data.status_code === 401) {
          localStorage.removeItem('adminId');
          localStorage.removeItem('adminApiKey');
          navigate("/admin/auth");
        }
      }
    },
    onError: (error) => {
      Utility.showToast("error", "Failed to fetch rooms data");
      setLoading(false);
    }
  });

  const fetchRooms = async () => {
    try {
      setLoading(true);
      
      const adminId = localStorage.getItem('adminId');
      const apiKey = localStorage.getItem('adminApiKey');
      
      const response = await fetch(`${BASE_URL}/api/admin/rooms`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          'admin-id': adminId,
          'api-key': apiKey
        }
      });
  
      const data = await response.json();
  
      if (data.status_code === 200) {
        setRooms(data.data.rooms || []);
        setLoading(false);
      } else {
        Utility.showToast("error", data.message);
        if (data.status_code === 401) {
          localStorage.removeItem('adminId');
          localStorage.removeItem('adminApiKey');
          localStorage.removeItem('adminEmail');
          navigate("/admin/auth");
        }
        setLoading(false);
      }
    } catch (error) {
      Utility.showToast("error", "Failed to fetch rooms data");
      setLoading(false);
    }
  };  

  // Handle admin logout
  const handleLogout = async () => {
    try {
      const response = await fetch("/api/admin/logout", {
        method: "POST",
        headers: {
          'admin-id': localStorage.getItem('adminId'),
          'api-key': localStorage.getItem('adminApiKey')
        }
      });
  
      const data = await response.json();
      
      // Clear localStorage regardless of response
      localStorage.removeItem("adminId");
      localStorage.removeItem("adminApiKey");
      localStorage.removeItem("adminEmail");
      
      if (data.status_code === 200) {
        Utility.showToast("success", data.message);
      }
      
      navigate("/admin/auth");
    } catch (error) {
      // Still clear localStorage on error
      localStorage.removeItem("adminId");
      localStorage.removeItem("adminApiKey");
      localStorage.removeItem("adminEmail");
      Utility.showToast("error", "Logged out successfully");
      navigate("/admin/auth");
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "available":
        return "bg-success";
      case "in-use":
        return "bg-warning";
      case "full":
        return "bg-danger";
      default:
        return "bg-secondary";
    }
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case "available":
        return "Available";
      case "in-use":
        return "In Use";
      case "full":
        return "Full";
      default:
        return status;
    }
  };

  // Calculate statistics
  const calculateStats = () => {
    const total = rooms.length;
    const available = rooms.filter(room => room.status === "available").length;
    const inUse = rooms.filter(room => room.status === "in-use").length;
    const full = rooms.filter(room => room.status === "full").length;
    
    return { total, available, inUse, full };
  };

  const stats = calculateStats();

  return (
    <div className="background">
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="text-white">Admin Dashboard</h1>
          <div>
            <button
              onClick={handleRestartServer}
              className="btn btn-danger semi-bold me-2"
              disabled={restartingServer || resettingAllRooms}
            >
              {restartingServer ? "Restarting..." : "Restart Server"}
            </button>
            <button 
              onClick={() => navigate("/admin/assets")}
              className="btn btn-info semi-bold me-2 ml-2"
              disabled={restartingServer}
            >
              Manage Assets
            </button>
            <button onClick={handleLogout} className="btn btn-info semi-bold ml-2" disabled={restartingServer}>
              Logout
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Total Rooms</h5>
                <h2>{stats.total}</h2>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Available</h5>
                <h2>{stats.available}</h2>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">In Use</h5>
                <h2>{stats.inUse}</h2>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Full</h5>
                <h2>{stats.full}</h2>
              </div>
            </div>
          </div>
        </div>

        {/* Rooms Table */}
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Room Status</h5>
            <div>
              <button
                onClick={handleResetAllRooms}
                className="btn btn-danger semi-bold me-2"
                disabled={loading || resettingAllRooms || restartingServer}
              >
                {resettingAllRooms ? "Processing..." : "Reset All Rooms"}
              </button>
              <button
                onClick={fetchRooms}
                className="btn btn-info semi-bold ml-2"
                disabled={resettingAllRooms || restartingServer}
              >
                Refresh
              </button>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-white" role="status">
                  <span className="visually-hidden"></span>
                </div>
              </div>
            ) : rooms.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">No rooms found</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Room ID</th>
                      <th>Name</th>
                      <th>Players</th>
                      <th>Capacity</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.map((room) => (
                      <tr key={room.id}>
                        <td>{room.id}</td>
                        <td>{room.name || `Room ${room.id}`}</td>
                        <td>{room.currentPlayers || 0}</td>
                        <td>{room.capacity || 4}</td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(room.status)}`}>
                            {getStatusText(room.status)}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => {
                              navigate(`/admin/room/${room.id}`);
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="text-center button-home-top">
          <button
            onClick={() => navigate("/")}
            className="btn border-home-button"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );

  async function handleResetAllRooms() {
    if (!window.confirm("Reset ALL rooms?\n\nWarning: Do NOT use this during an ongoing game — it can cause game flow issues for connected players.\n\nUse this only when data did not update correctly in the database (e.g., due to network/socket issues) and you need to force-clean rooms.\n\nIf you just want to remove a player, use the Remove Member option in the admin room details instead.\n\nThis will remove all members and clear all room assets/statistics across every room.")) {
      return;
    }

    try {
      setResettingAllRooms(true);
      const adminId = localStorage.getItem("adminId");
      const apiKey = localStorage.getItem("adminApiKey");

      const response = await fetch(`${BASE_URL}/api/admin/rooms/reset-all`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "admin-id": adminId,
          "api-key": apiKey,
        },
      });

      const data = await response.json();

      if (data.status_code === 200) {
        Utility.showToast("success", "All rooms reset successfully");
        fetchRooms();
      } else {
        Utility.showToast("error", data.message || "Failed to reset all rooms");
        if (data.status_code === 401) {
          localStorage.removeItem("adminId");
          localStorage.removeItem("adminApiKey");
          localStorage.removeItem("adminEmail");
          navigate("/admin/auth");
        }
      }
    } catch (error) {
      Utility.showToast("error", "Failed to reset all rooms");
    } finally {
      setResettingAllRooms(false);
    }
  }

  async function handleRestartServer() {
    if (!window.confirm("Restart server now? This will run pm2 restart all on the server.")) {
      return;
    }

    try {
      setRestartingServer(true);
      const adminId = localStorage.getItem("adminId");
      const apiKey = localStorage.getItem("adminApiKey");

      const response = await fetch(`${BASE_URL}/api/admin/server/restart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "admin-id": adminId,
          "api-key": apiKey,
        },
      });

      const data = await response.json();

      if (data.status_code === 200) {
        Utility.showToast("success", "Restart scheduled");
      } else {
        Utility.showToast("error", data.message || "Failed to restart server");
        if (data.status_code === 401) {
          localStorage.removeItem("adminId");
          localStorage.removeItem("adminApiKey");
          localStorage.removeItem("adminEmail");
          navigate("/admin/auth");
        }
      }
    } catch (error) {
      Utility.showToast("error", "Failed to restart server");
    } finally {
      setRestartingServer(false);
    }
  }
};

export default AdminDashboard;
